import { format } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';

// Default timezone - can be overridden by company settings
export const DEFAULT_TIMEZONE = 'Australia/Sydney';

// Convert UTC date to specified timezone
export const toTimezone = (date: Date | string, timezone: string) => {
  return toZonedTime(date, timezone);
};

// Convert timezone date to UTC (for sending to database)
export const fromTimezone = (date: Date, timezone: string) => {
  return fromZonedTime(date, timezone);
};

// Format date in specified timezone
export const formatInTz = (date: Date | string, timezone: string, formatStr: string) => {
  return formatInTimeZone(date, timezone, formatStr);
};

// Get current date/time in specified timezone
export const nowInTimezone = (timezone: string) => {
  return toZonedTime(new Date(), timezone);
};

// Get start of day in specified timezone (returns UTC Date for database queries)
export const startOfDayInTz = (date: Date, timezone: string) => {
  const zonedDate = toZonedTime(date, timezone);
  zonedDate.setHours(0, 0, 0, 0);
  return fromZonedTime(zonedDate, timezone);
};

// Get end of day in specified timezone (returns UTC Date for database queries)
export const endOfDayInTz = (date: Date, timezone: string) => {
  const zonedDate = toZonedTime(date, timezone);
  zonedDate.setHours(23, 59, 59, 999);
  return fromZonedTime(zonedDate, timezone);
};

/** First day of the week: 1 = Monday (AU/NZ/UK default), 0 = Sunday (US/CA). */
export type WeekStart = 0 | 1;

// Get start of week in specified timezone
export const startOfWeekInTz = (date: Date, timezone: string, weekStartsOn: WeekStart = 1) => {
  const zonedDate = toZonedTime(date, timezone);
  const day = zonedDate.getDay(); // 0 = Sunday
  const diff = -((day - weekStartsOn + 7) % 7);
  zonedDate.setDate(zonedDate.getDate() + diff);
  zonedDate.setHours(0, 0, 0, 0);
  return fromZonedTime(zonedDate, timezone);
};

// Get end of week in specified timezone (6 days after the start)
export const endOfWeekInTz = (date: Date, timezone: string, weekStartsOn: WeekStart = 1) => {
  const zonedDate = toZonedTime(date, timezone);
  const day = zonedDate.getDay();
  const diff = 6 - ((day - weekStartsOn + 7) % 7);
  zonedDate.setDate(zonedDate.getDate() + diff);
  zonedDate.setHours(23, 59, 59, 999);
  return fromZonedTime(zonedDate, timezone);
};

// Get start of month in specified timezone
export const startOfMonthInTz = (date: Date, timezone: string) => {
  const zonedDate = toZonedTime(date, timezone);
  zonedDate.setDate(1);
  zonedDate.setHours(0, 0, 0, 0);
  return fromZonedTime(zonedDate, timezone);
};

// Get end of month in specified timezone
export const endOfMonthInTz = (date: Date, timezone: string) => {
  const zonedDate = toZonedTime(date, timezone);
  zonedDate.setMonth(zonedDate.getMonth() + 1);
  zonedDate.setDate(0); // Last day of previous month
  zonedDate.setHours(23, 59, 59, 999);
  return fromZonedTime(zonedDate, timezone);
};

// Subtract days - simply moves the date backwards in time
// The timezone conversion should be done by startOfDayInTz/endOfDayInTz after this
export const subDays = (date: Date, days: number): Date => {
  const msPerDay = 24 * 60 * 60 * 1000;
  return new Date(date.getTime() - (days * msPerDay));
};

// Subtract months - simply moves the date backwards by months
// The timezone conversion should be done by startOfDayInTz/endOfDayInTz after this
export const subMonths = (date: Date, months: number): Date => {
  const result = new Date(date.getTime());
  result.setMonth(result.getMonth() - months);
  return result;
};

// Parse a date string (YYYY-MM-DD) as start of day in specified timezone
// This avoids browser timezone dependency - the string is treated as a date in the target timezone.
// (The old noon-UTC trick landed a day late in UTC+12/+13, e.g. New Zealand in summer.)
export const parseDateInTz = (dateString: string, timezone: string): Date => {
  return fromZonedTime(`${dateString.slice(0, 10)}T00:00:00`, timezone);
};

// Format a date to YYYY-MM-DD string in specified timezone (for input value)
export const formatDateInputInTz = (date: Date, timezone: string): string => {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
};

// ============================================
// PICKED CALENDAR DAYS
// A <Calendar> pick is midnight in the BROWSER's timezone, so the day the user chose is the
// Date's local fields. Never run a pick through startOfDayInTz / formatDateInputInTz: when the
// browser is ahead of the company (a Sydney CSR on a Los Angeles tenant) it lands a day early.
// ============================================

/** "yyyy-MM-dd" of a day picked on a calendar. */
export const pickedDayKey = (date: Date): string => format(date, 'yyyy-MM-dd');

/** Midnight in the company timezone of a day picked on a calendar (UTC Date for the database). */
export const pickedDayStartInTz = (date: Date, timezone: string): Date =>
  parseDateInTz(pickedDayKey(date), timezone);

/** A "yyyy-MM-dd" day as a calendar value (browser-local midnight), to preselect or bound a picker. */
export const dayKeyToPickerDate = (dayKey: string): Date => {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// ============================================
// LEGACY COMPATIBILITY FUNCTIONS
// These functions use the default timezone for backward compatibility
// New code should use the timezone-parameterized versions above
// ============================================

/** @deprecated Use toTimezone(date, timezone) instead */
export const toAustraliaTime = (date: Date | string) => {
  return toTimezone(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use fromTimezone(date, timezone) instead */
export const fromAustraliaTime = (date: Date) => {
  return fromTimezone(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use formatInTz(date, timezone, formatStr) instead */
export const formatAustraliaTime = (date: Date | string, formatStr: string) => {
  return formatInTz(date, DEFAULT_TIMEZONE, formatStr);
};

/** @deprecated Use nowInTimezone(timezone) instead */
export const nowInAustralia = () => {
  return nowInTimezone(DEFAULT_TIMEZONE);
};

/** @deprecated Use startOfDayInTz(date, timezone) instead */
export const startOfDayAustralia = (date: Date) => {
  return startOfDayInTz(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use endOfDayInTz(date, timezone) instead */
export const endOfDayAustralia = (date: Date) => {
  return endOfDayInTz(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use startOfWeekInTz(date, timezone) instead */
export const startOfWeekAustralia = (date: Date) => {
  return startOfWeekInTz(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use endOfWeekInTz(date, timezone) instead */
export const endOfWeekAustralia = (date: Date) => {
  return endOfWeekInTz(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use startOfMonthInTz(date, timezone) instead */
export const startOfMonthAustralia = (date: Date) => {
  return startOfMonthInTz(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use endOfMonthInTz(date, timezone) instead */
export const endOfMonthAustralia = (date: Date) => {
  return endOfMonthInTz(date, DEFAULT_TIMEZONE);
};

/** @deprecated Use subDays(date, days) instead */
export const subDaysAustralia = (date: Date, days: number) => {
  return subDays(date, days);
};

/** @deprecated Use subMonths(date, months) instead */
export const subMonthsAustralia = (date: Date, months: number) => {
  return subMonths(date, months);
};

/** @deprecated Use parseDateInTz(dateString, timezone) instead */
export const parseAustraliaDate = (dateString: string): Date => {
  return parseDateInTz(dateString, DEFAULT_TIMEZONE);
};

/** @deprecated Use formatDateInputInTz(date, timezone) instead */
export const formatAustraliaDateInput = (date: Date): string => {
  return formatDateInputInTz(date, DEFAULT_TIMEZONE);
};

// Constant for backward compatibility
export const AUSTRALIA_TIMEZONE = DEFAULT_TIMEZONE;
